import { Component, Inject, OnInit } from "@angular/core";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { AuthRequest } from "@dto/authRequest";
import { AuthenticationService } from "@services/authentication/authentication.service";
import { FormService } from "@services/form/form.service";
import { MessageService } from "@services/message/message.service";
import { CustomersService } from "@services/entity/customers.service";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ReleaseNotesDialogComponent } from "../../dialog/release-notes-dialog/release-notes-dialog.component";
import { StripeInfosService } from "@services/entity/stripe-infos.service";
import { StripeInfo } from "@models/stripeInfo";
import { Roles } from "../../../enums/roles.enum";
import { isEmpty } from "rxjs/operators";
import { isNullOrUndefined } from "is-what";
import { HttpClient } from "@angular/common/http";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  private _returnUrl: string;
  public _message: string;

  public currentVersion: string;
  public latestVersion: string;
  public hideReleaseNotes: boolean;
  public deliveryNote: boolean;

  public whitelabel: string;
  public whitelabelURL: string =
    "https://cubosgeneralstorage.blob.core.windows.net/whitelabel-storage/";

  currentYearLong(): number {
    return new Date().getFullYear();
  }

  constructor(
    @Inject("BASE_URL") private baseUrl: string,
    private authenticationService: AuthenticationService,
    private stripeInfosService: StripeInfosService,
    public formService: FormService,
    public messageService: MessageService,
    public customersService: CustomersService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private spinner: NgxSpinnerService,
    private http: HttpClient,
    private dialog: NgbModal
  ) {}

  ngOnInit(): void {
    this.formService.form = new UntypedFormGroup({
      username: new UntypedFormControl("", [Validators.required]),
      password: new UntypedFormControl("", [Validators.required]),
    });

    this._returnUrl =
      this.activatedRoute.snapshot.queryParams["returnUrl"] || "/";
    this._message = this.activatedRoute.snapshot.queryParams["message"] || "";
  }

  ngAfterViewInit() {
    this.whitelabel = localStorage.getItem("whitelabel");
  }

  public loginUser(loginFormValue) {
    this.spinner.show();
    this.messageService.clearErrors();
    const login = { ...loginFormValue };

    const authRequest: AuthRequest = {
      email: login.username,
      password: login.password,
      //clientURI: this.baseUrl + 'auth/forgotpassword',
    };

    this.authenticationService.loginUser(authRequest).subscribe(
      (result) => {
        localStorage.setItem("accessToken", result.accessToken);
        localStorage.setItem("refreshToken", result.refreshToken);
        localStorage.setItem("userId", result.userId);
        localStorage.setItem("customerId", result.customerId.toString());
        this.authenticationService.sendAuthStateChangeNotification(
          result.isAuthSuccessful
        );

        if (
          this.authenticationService.userHasRole(Roles.Operator) &&
          (this._returnUrl === "/" || !this._returnUrl)
        ) {
          localStorage.setItem("pool-url", "");
          this.customersService
            .getCustomerByName("CUBOS-Pool")
            .then((pool) =>
              localStorage.setItem("pool-url", "/pool/" + pool.id.toString())
            );
          this._returnUrl = "/operator";
        }
        this.router.navigate([this._returnUrl]);
        this.spinner.hide();
        this.checkVersion();
      },
      (error) => {
        console.log(error);
        if (isNullOrUndefined(error)) {
          this.messageService.setError(
            "Das hat nicht funktioniert... Bitte prüfen Sie Ihre Internetverbindung."
          );
        } else {
          this.messageService.setError(error);
        }
        this.spinner.hide();
      }
    );
  }

  checkVersion() {
    this.fetchLatestVersionFromApi((latest) => {
      this.latestVersion = latest;
      this.currentVersion = localStorage.getItem("appVersion");
      this.hideReleaseNotes = localStorage.getItem("hideReleaseNotes") === "true";

      if (this.currentVersion == null || this.currentVersion === "undefined") {
        localStorage.setItem("appVersion", this.latestVersion);
        return;
      }

      if (this.isCurrentVersionUpToDate(this.currentVersion, this.latestVersion)) {
        console.log(
          `CUBOS.Connect version ${this.currentVersion} is up-to-date.`
        );
        if (!this.hideReleaseNotes) {
          this.openReleaseNotesDialog();
        }
        return;
      }

      this.openReleaseNotesDialog();
    });
  }

  private fetchLatestVersionFromApi(onVersionResolved: (latestVersion: string) => void) {
    this.http.get<{ version: string }>(`${this.baseUrl}api/AppInfo/Version`).subscribe(
      (result) => {
        const latest = this.normalizeVersion(result?.version);
        onVersionResolved(latest);
      },
      (_) => {
        // fallback if API endpoint is temporarily unavailable
        onVersionResolved(this.normalizeVersion(localStorage.getItem("appVersion") ?? "0.0.0"));
      }
    );
  }

  private normalizeVersion(version: string): string {
    if (!version) {
      return "0.0.0";
    }

    const base = version.split("+")[0].split("-")[0];
    const numericParts = base
      .split(".")
      .map((part) => parseInt(part, 10))
      .filter((part) => !isNaN(part));

    while (numericParts.length < 3) {
      numericParts.push(0);
    }

    return numericParts.slice(0, 3).join(".");
  }

  private isCurrentVersionUpToDate(currentVersion: string, latestVersion: string): boolean {
    const current = this.normalizeVersion(currentVersion)
      .split(".")
      .map((part) => parseInt(part, 10));
    const latest = this.normalizeVersion(latestVersion)
      .split(".")
      .map((part) => parseInt(part, 10));

    for (let i = 0; i < 3; i++) {
      if (current[i] > latest[i]) {
        return true;
      }
      if (current[i] < latest[i]) {
        return false;
      }
    }

    return true;
  }

  openReleaseNotesDialog() {
    console.log(
      `CUBOS.Connect was updated from ${this.currentVersion} to version ${this.latestVersion}.`
    );
    this.currentVersion = this.latestVersion;
    localStorage.setItem("appVersion", this.latestVersion);
    localStorage.setItem("hideReleaseNotes", "true");

    window.setTimeout(() => {
      const dialogRef = this.dialog.open(ReleaseNotesDialogComponent, {
        centered: true,
        size: "xl",
        backdrop: "static",
      });
    }, 2000);
  }
}
